import { useState, useMemo, useCallback } from "react"; 
import { motion, AnimatePresence } from "framer-motion";
import { Search, Clock, Trash2 } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { useI18n } from "@/lib/i18n";
import { useAppData, emitDataChanged } from "@/hooks/useAppData";
import { deleteHistoryItem, clearHistory } from "@/lib/db";
import { useClipboard } from "@/hooks/useClipboard";
import { emitPreviewRequest } from "@/events/previewBus";
import { useGlobalSelection } from "@/hooks/useGlobalSelection";
import { deleteSelectedItems } from "@/lib/deleteActions";
import ItemContextMenu from "@/components/shared/ItemContextMenu";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HISTORY_MAX_ITEMS } from "@/types";
import { formatTime, stripMarkupAndMarkdown } from "@/lib/textUtils";
import type { DragPayload } from "@/components/shared/DndProvider";

import { DraggableHistoryItem } from "@/components/areas/DraggableHistoryItem";
import { HistoryItemRow } from "@/components/areas/HistoryItemRow";

const HistoryArea = () => {
  const { t } = useI18n();
  const { history, getDataById, loading } = useAppData();
  const { copyToClipboard } = useClipboard();
  const globalSelection = useGlobalSelection();
  const [search, setSearch] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const validHistory = useMemo(() => {
    return history.filter((h) => !!getDataById(h.dataId));
  }, [history, getDataById]);

  const filteredHistory = useMemo(() => {
    if (!search.trim()) return validHistory;
    const q = search.toLowerCase();
    return validHistory.filter((h) => {
      const data = getDataById(h.dataId)!;
      return (
        data.label.toLowerCase().includes(q) ||
        (data.type === "text" && data.content.toLowerCase().includes(q))
      );
    });
  }, [validHistory, getDataById, search]);

  const handleItemClick = useCallback(async (itemId: string, dataId: string, e: React.MouseEvent) => {
    const isMulti = e.metaKey || e.ctrlKey;
    const data = getDataById(dataId);
    if (!data) return;

    if (isMulti) {
      const isSelected = globalSelection.entries.some(en => en.itemId === itemId);
      if (isSelected) {
        globalSelection.removeEntries([itemId]);
      } else {
        globalSelection.addEntries([{ area: 'history', itemId, dataId }]);
      }
    } else {
      globalSelection.clearAll();
      globalSelection.addEntries([{ area: 'history', itemId, dataId }]);
    }

    const isHtml = data.mimeType === "text/html" || (data.type === "text" && /^\s*<[a-z][\s\S]*>/i.test(data.content));
    const clipType = data.type === "image" ? "image" : isHtml ? "html" : "text";
    const ok = await copyToClipboard(data.content, clipType === "html" ? "html" : clipType);
    if (ok) {
      toast.success(t("menu.copy") + " ✓");
      emitPreviewRequest({
        id: data.id,
        type: clipType as "text" | "image" | "html",
        content: data.content,
      });
    }
  }, [getDataById, copyToClipboard, t, globalSelection]);

  const handleRightClickSelect = useCallback((itemId: string, dataId: string) => {
    const isSelected = globalSelection.entries.some(en => en.itemId === itemId);
    if (!isSelected) {
      globalSelection.clearAll();
      globalSelection.addEntries([{ area: 'history', itemId, dataId }]);
    }
  }, [globalSelection]);

  const handleDeleteSelected = useCallback(async () => {
    const historyEntries = globalSelection.entries.filter(e => e.area === 'history');
    if (historyEntries.length === 0) return;
    await deleteSelectedItems(historyEntries);
    globalSelection.clearArea('history');
    setShowDeleteDialog(false);
  }, [globalSelection]);

  const handleDeleteOne = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteHistoryItem(id);
    emitDataChanged();
  };

  const selectedHistoryIds = new Set(
    globalSelection.entries.filter(e => e.area === 'history').map(e => e.itemId)
  );

  return (
    <>
      <motion.section
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="glass-panel-static rounded-2xl p-4 flex flex-col h-full min-h-0 overflow-hidden"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("history.search")}
              data-testid="history-search-input"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-background/40 border border-border/50 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <span className="text-xs text-muted-foreground/60 shrink-0">
            {validHistory.length} / {HISTORY_MAX_ITEMS}
          </span>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground/60">
              <Clock className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-xs whitespace-pre-line">{t("history.empty")}</p>
            </div>
          </div>
        ) : (
          <ScrollArea className="flex-1 min-h-0 h-0 overflow-x-hidden">
            <div className="flex flex-col gap-1.5 pr-2">
              <AnimatePresence mode="popLayout">
                {filteredHistory.map((item) => {
                  const data = getDataById(item.dataId);
                  if (!data) return null;

                  return (
                    <DraggableHistoryItem
                      key={item.id}
                      itemId={item.id}
                      dataId={item.dataId}
                      globalEntries={globalSelection.entries}
                    >
                      <ItemContextMenu
                        area="history"
                        itemId={item.id}
                        dataId={item.dataId}
                        onRightClickSelect={handleRightClickSelect}
                        onDelete={() => setShowDeleteDialog(true)}
                      >
                        <HistoryItemRow
                          itemId={item.id}
                          data={data}
                          createdAt={item.createdAt}
                          isSelected={selectedHistoryIds.has(item.id)}
                          onClick={(e) => handleItemClick(item.id, item.dataId, e)}
                          onDelete={(e) => handleDeleteOne(item.id, e)}
                        />
                      </ItemContextMenu>
                    </DraggableHistoryItem>
                  );
                })}
              </AnimatePresence>
            </div>
          </ScrollArea>
        )}
      </motion.section>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteSelected}
        count={globalSelection.entries.filter(e => e.area === 'history').length}
      />
    </>
  );
};

export default HistoryArea;
